"use client";

import { PieChart, Pie, Cell, Label, Sector } from "recharts";
import { PieSectorDataItem } from "recharts/types/polar/Pie";
import {
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart"; // Assuming shadcn path, adjust if necessary

interface AssessmentDecisionChartProps {
  promoteCount: number;
  deferCount: number;
}

// Hardcoded data for initial rendering
const initialPromoteCount = 3; // Example value
const initialDeferCount = 2; // Example value

export function AssessmentDecisionChart({
  promoteCount = initialPromoteCount,
  deferCount = initialDeferCount,
}: Partial<AssessmentDecisionChartProps>) {
  const totalDecisions = promoteCount + deferCount;
  const chartConfig = {
    promote: {
      label: "Promote",
      color: "hsl(var(--chart-1))",
    },
    defer: {
      label: "Defer",
      color: "hsl(var(--chart-2))",
    },
  };

  const chartData = [
    { name: "Promote", value: promoteCount, fill: chartConfig.promote.color },
    { name: "Defer", value: deferCount, fill: chartConfig.defer.color },
  ];

  // Determine activeIndex: highlight the larger slice, or Promote if equal/only one
  let activeIndex = 0;
  if (promoteCount === 0 && deferCount > 0) {
    activeIndex = 1;
  } else if (promoteCount > 0 && deferCount > 0) {
    activeIndex = promoteCount >= deferCount ? 0 : 1;
  } else if (promoteCount === 0 && deferCount === 0 && totalDecisions > 0) {
    // This case should ideally not happen if totalDecisions > 0 means one of them is > 0
    // but as a fallback, if chartData has items, an index must be chosen.
    // However, the main `if (totalDecisions === 0)` check handles empty chart.
    activeIndex = 0;
  }

  if (totalDecisions === 0) {
    return (
      <div
        data-testid="assessment-decision-chart"
        className="mx-auto flex aspect-square max-h-[300px] items-center justify-center"
      >
        <p className="text-muted-foreground">No decisions yet</p>
      </div>
    );
  }

  return (
    <ChartContainer
      config={chartConfig}
      data-testid="assessment-decision-chart"
      className="mx-auto aspect-square w-full max-w-[300px]"
    >
      <PieChart>
        <ChartTooltip
          cursor={false}
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const data = payload[0];
              return (
                <div className="rounded-lg border bg-background p-2 shadow-md">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2 w-2 rounded-sm"
                      style={{ backgroundColor: data.payload.fill }}
                    />
                    <span className="text-xs">
                      {data.name}
                    </span>
                    <span className="text-xs font-semibold ml-1">
                      {data.value}
                    </span>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          innerRadius={80}
          outerRadius={120}
          strokeWidth={5}
          paddingAngle={totalDecisions > 1 ? 5 : 0}
          activeIndex={activeIndex}
          activeShape={({ outerRadius = 0, ...props }: PieSectorDataItem) => (
            <Sector {...props} outerRadius={outerRadius + 15} />
          )}
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.fill}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          ))}
          <Label
            content={({ viewBox }) => {
              if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className="fill-foreground text-7xl font-bold"
                    >
                      {totalDecisions.toLocaleString()}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) + 40}
                      className="fill-muted-foreground text-sm"
                    >
                      DECISIONS
                    </tspan>
                  </text>
                );
              }
              return null;
            }}
            position="center"
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  );
}
